import { useCallback, useEffect, useMemo, useState } from 'react';

import { buildOpportunityRequestDetailsRhfValues } from '../../../validation/opportunityProjectFieldsSchema';

import type { ProjectInformationProps } from './subsections/ProjectInformation';
import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { OpportunityInformationTeamProps } from '../../../components/steps/OpportunityInformationTeam';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { Request } from '../../../types';
import type { FormValidationError } from '../../../utils/formValidation';

/** New-request flow: opportunity project fields + team blocks (no `request` entity prop). */
export type OpportunityInformationInitialProps = ProjectInformationProps;

/** Same UI when a saved request is shown (view or edit). */
export interface OpportunityInformationPersistedProps {
  request: Request;
  isEditing: boolean;
  editedData: Record<string, unknown>;
  updateEditedData: (
    field: string,
    value: string | string[] | number | boolean,
  ) => void;
  /** Passed through by `RequestDetail`; basic/advanced visibility may be wired later. */
  showFullDetails?: boolean;
  showViewToggle?: boolean;
  isAdvancedView?: boolean;
  onToggleView?: () => void;
  onRefetchAttachments?: () => void;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  disabled?: boolean;
  /** Set when an opened-request save is blocked on Partner Name. */
  validationErrors?: FormValidationError[];
  rhfValidationRevision?: number;
}

export type OpportunityInformationProps =
  | OpportunityInformationInitialProps
  | OpportunityInformationPersistedProps;

export function isOpportunityInformationPersistedProps(
  p: OpportunityInformationProps,
): p is OpportunityInformationPersistedProps {
  return (
    'request' in p &&
    (p as OpportunityInformationPersistedProps).request != null
  );
}

/**
 * View-model when props include a persisted `request` (merged draft values, RHF remount revision).
 */
export function useOpportunityInformationPersisted(
  props: OpportunityInformationPersistedProps,
) {
  const {
    request,
    isEditing,
    editedData,
    updateEditedData,
    attachmentsBySection = {},
    onAttachFiles,
    onDeleteAttachment,
    selectedFiles = {},
    onFilesSelected,
    offlineMode = false,
    disabled = false,
    showFullDetails = true,
    onCaptionChange,
    pendingCaptionEdits,
  } = props;

  const formData = useMemo(() => {
    const base = { ...request, ...editedData } as Record<string, unknown>;
    return base as OpportunityInformationTeamProps['formData'];
  }, [request, editedData]);

  const updateFormData = useCallback(
    (field: string, value: unknown) => {
      if (typeof value === 'boolean') {
        updateEditedData(field, value);
        return;
      }
      updateEditedData(field, value as string | string[] | number);
    },
    [updateEditedData],
  );

  const fieldDisabled = !isEditing || disabled;
  const requestIdStr = request.id != null ? String(request.id) : undefined;

  const requestProjectDetailsSnapshot = useMemo(
    () =>
      JSON.stringify(
        buildOpportunityRequestDetailsRhfValues({
          ...(request as Record<string, unknown>),
        }),
      ),
    [request],
  );

  const [parentSyncRevision, setParentSyncRevision] = useState<
    number | undefined
  >(undefined);
  useEffect(() => {
    setParentSyncRevision(n => (n == null ? 1 : n + 1));
  }, [request.id]);

  useEffect(() => {
    if (isEditing) {
      return;
    }
    setParentSyncRevision(n => (n == null ? 1 : n + 1));
  }, [isEditing, requestProjectDetailsSnapshot]);

  return {
    formData,
    updateFormData,
    fieldDisabled,
    requestIdStr,
    requestProjectDetailsSnapshot,
    parentSyncRevision,
    attachmentsBySection,
    onAttachFiles,
    onDeleteAttachment,
    selectedFiles,
    onFilesSelected,
    offlineMode,
    showFullDetails,
    onCaptionChange,
    pendingCaptionEdits,
    disabled,
    request,
    isEditing,
    editedData,
    updateEditedData,
  };
}
